import { useSpeakerHeadshot } from '@src/hooks/useSpeakerHeadshot';
import {
  useAnalytics,
  useStore,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { AdditionalAspectRatio, AspectRatio } from '@src/types.ts/video';
import {
  applySpeakerControlValueMappingToControls,
  normalizeSpeakerForControlBindings,
} from '@src/utility/compositionSettings';
import { downloadMediaFromSource } from '@src/utility/mediaGeneration';
import { carryCardSpeakerLabel } from '@src/utility/quoteCardSpeakerLabel';
import { formatStoryteller } from '@src/utility/shareableImages';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import CloseIcon from '../../../svgs/CloseIcon';
import PlayIconPure from '../../../svgs/PlayIconPure';
import { getPosterUrl } from '../../../utility/general';
import { useFlagsCombination } from '../../../utility/useFlagsCombination';
import { AutosaveStatusIcon } from '../../common/AutosaveStatusIcon';
import { AutosaveStatusIndicator } from '../../common/AutosaveStatusIndicator';
import { UndoRedoButtons } from '../../common/UndoRedoButtons';
import Modal from '../../Modal';
import { PlayerOverlay } from '../../stage/PlayerOverlay';
import ClipDownloadDropdown from '../ClipDownloadDropdown';
import { CLIP_PREVIEW_Z } from '../clipPreviewZ';
import ContentButton from '../ContentButton';
import DimensionsSelector from '../DimensionsSelector';
import { QuoteCardSettingsPanel } from './QuoteCardSettingsPanel';
import { QuoteCardTabs } from './QuoteCardTabs';
import { QuoteCardTemplatesPanel } from './QuoteCardTemplatesPanel';
import { QuoteCardTranscriptPanel } from './QuoteCardTranscriptPanel';
import { usePreviewSupersample } from './usePreviewSupersample';
import { useQuoteCardAutoSave } from './useQuoteCardAutoSave';
import { useQuoteCardRenderer } from './useQuoteCardRenderer';

import type { ShareableImageType } from '../../../types.ts/story';
import type { MediaFormat } from '../../common/FormatDropdown';
import type { CompositionTemplate } from '../../sidepanel/CompositionSettings/types';
import type { Speaker } from '../SpeakerTypes';
import type { TabType } from './QuoteCardTabs';
import type { ExtendedAspectRatio } from '@src/types.ts/video';

type Props = {
  data: Pick<
    ShareableImageType,
    | 'id'
    | 'quote'
    | 'storytellerName'
    | 'imagefile'
    | 'speaker'
    | 'metadata'
    | 'source'
  >;
  onClose: () => void;
  storyteller?: string;
  isSaved: boolean;
  compositionTemplate: CompositionTemplate;
  source: Record<string, any>;
  onSaved: (
    id: string,
    source: Record<string, any>,
    composition: CompositionTemplate,
  ) => void;
  onAfterCreate: (composition: CompositionTemplate) => void;
};

const getOffsetsAndDimContainerRatio = (dimension: string): number => {
  switch (dimension) {
    case AspectRatio.AR_1_1:
      return 1 / 1;
    case AspectRatio.AR_16_9:
      return 16 / 9;
    case AspectRatio.AR_9_16:
      return 9 / 16;
    case AdditionalAspectRatio.AR_4_5:
      return 4 / 5;
    default:
      return 1 / 1;
  }
};
const EditSharableImageModal = (props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const userIdentity = useUserIdentity();
  const { contentStudioQuotesHide169And916 } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const [activeTab, setActiveTab] = useState<TabType>('settings');

  const {
    data,
    onClose,
    storyteller: storytellerFromStory,
    isSaved,
    onAfterCreate,
    onSaved,
  } = props;
  const [isDownloading, setIsDownloading] = useState(false);
  const { name, org } = formatStoryteller(
    data.storytellerName || storytellerFromStory,
  );
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const background = getPosterUrl(
    props.data.imagefile?.url || '',
    1080,
    1080,
    false,
  );

  const speakerHeadshotUrl = useSpeakerHeadshot({
    speakerId: data.speaker?.id,
    speakerName: name,
    storyId: videoCreator.story?.id,
    datoClientStore: videoCreator.datoClientStore,
  });

  const analyticsStore = useAnalytics();
  const [metadata, setMetadata] = useState(props.data.metadata);
  const sidebarRef = useRef<HTMLDivElement>(null);
  // Initialize Creatomate renderer using custom hook

  const transactionId = useRef<string | null>(null);
  useEffect(() => {
    transactionId.current = uuid().substring(0, 8);
  }, []);

  const {
    rendererContainerRef,
    activeElement,
    activeComposition,
    editorStoreRef,
    rendererRef,
    canUndo,
    canRedo,
    editRevision,
    updateAspectRatio,
    currentAspectRatio,
    activeCompositionTemplate,
    adoptTemplate,
    preview,
    replaceCompositionElement,
    compositionReady,
  } = useQuoteCardRenderer({
    text: data.quote,
    background,
    name: name,
    org: org,
    headshot: speakerHeadshotUrl,
    mode: 'interactive',
    compositionTemplate: props.compositionTemplate,
    quoteCardSource: props.source || undefined,
    setActiveTab,
  });

  const { footprintRef, width, height, scale } = usePreviewSupersample();

  // A template switch gives the card composition a new id; keep the Speaker
  // dropdown's selection with it.
  const compositionId: string | undefined = activeComposition?.source.id;
  const lastCompositionIdRef = useRef(compositionId);
  useEffect(() => {
    if (!compositionId) return;
    const previousId = lastCompositionIdRef.current;
    lastCompositionIdRef.current = compositionId;
    if (!previousId || previousId === compositionId) return;
    setMetadata(prev => carryCardSpeakerLabel(prev, previousId, compositionId));
  }, [compositionId]);

  // --- Autosave ---
  const [currentId, setCurrentId] = useState(data.id);
  const [isSavedLocal, setIsSavedLocal] = useState(isSaved);
  useEffect(() => {
    setCurrentId(data.id);
    setIsSavedLocal(isSaved);
  }, [data.id, isSaved]);

  const handleFirstSave = useCallback(
    (newId: string, source: Record<string, any>, templateId: string) => {
      const formattedStoryteller = `${name}|${org}`;
      const saveMetadata = {
        ...(metadata || {}),
        editedBy: userIdentity.currentEditor,
      };
      if (videoCreator.story) {
        videoCreator.story.shareableImages = [
          ...(videoCreator.story.shareableImages || []),
          {
            quote: data.quote,
            storytellerName: formattedStoryteller,
            imagefile: data.imagefile,
            id: newId,
            source,
            parentComposition: { id: templateId },
            metadata: saveMetadata,
            _updatedAt: new Date().toISOString(),
          },
        ] as ShareableImageType[];
        videoCreator.unsavedShareableImages =
          videoCreator.unsavedShareableImages?.filter(o => o.id !== data.id) ||
          [];
        void videoCreator.updateStory(videoCreator.story);
      }
      setCurrentId(newId);
      setIsSavedLocal(true);
      props.onSaved(newId, source, {
        ...activeCompositionTemplate,
        id: templateId,
      });
    },
    [
      name,
      org,
      metadata,
      userIdentity.currentEditor,
      videoCreator,
      data.id,
      data.quote,
      data.imagefile,
      activeCompositionTemplate,
      props.onSaved,
    ],
  );

  const handleAutoSaved = useCallback(
    (source: Record<string, any>, templateId: string) => {
      if (videoCreator.story?.shareableImages) {
        videoCreator.story.shareableImages =
          videoCreator.story.shareableImages.map(s =>
            s.id === currentId
              ? {
                  ...s,
                  source,
                  parentComposition: { id: templateId },
                  _updatedAt: new Date().toISOString(),
                }
              : s,
          );
      }
      // Notify parent of the autosave so card renderers update immediately
      onSaved(currentId, source, { id: templateId } as CompositionTemplate);
    },
    [videoCreator, currentId, onSaved],
  );

  const { status, lastSavedAt, flush, requestSave } = useQuoteCardAutoSave({
    editorStoreRef,
    rendererRef,
    compositionReady,
    isSaved: isSavedLocal,
    story: videoCreator.story,
    saveParams: {
      id: currentId,
      quote: data.quote,
      imagefile: data.imagefile,
      speakerId: data.speaker?.id,
      name,
      org,
      currentEditor: userIdentity.currentEditor,
    },
    activeTemplateId: activeCompositionTemplate.id,
    metadata,
    storyRepository: datoClientStore.storyRepository,
    onFirstSave: handleFirstSave,
    onAutoSaved: handleAutoSaved,
  });

  const handleClose = useCallback(() => {
    // Blur active element to commit any pending text field edits before saving
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // Allow the blur handler and editor observer to process before flushing
    setTimeout(async () => {
      await flush();
      onClose();
    }, 0);
  }, [flush, onClose]);

  // Keep working from what a save returns: the next Set as Graphic Standard
  // starts from it, and after Save New Template the card moves to the new
  // template (PLAT-11977).
  const handleTemplateSaved = (composition: CompositionTemplate) => {
    const isNewTemplate = composition.id !== activeCompositionTemplate.id;
    adoptTemplate(composition);
    if (isNewTemplate) requestSave();
    onAfterCreate(composition);
  };

  const handleAddComposition = (composition: CompositionTemplate) => {
    if (composition.id !== activeCompositionTemplate?.id) {
      // Flush any pending autosave for the current template before switching
      void flush();
      replaceCompositionElement(composition);
    }
  };

  const handleSpeakerControlChange = (
    inputMapping: Record<string, string>,
    speaker: Speaker,
  ) => {
    if (!editorStoreRef.current || !compositionId) return;
    const plain: Record<string, unknown> = {};
    Object.entries(speaker).forEach(([k, v]) => {
      plain[k] = v;
    });
    // The headshot binding reads `headshot`; the speaker carries `headshots[]`.
    normalizeSpeakerForControlBindings(plain);

    // The card composition, not a child selected on the canvas: the bindings'
    // paths and the dropdown's speakerLabel are keyed to it.
    applySpeakerControlValueMappingToControls(
      compositionId,
      editorStoreRef.current,
      activeCompositionTemplate?.controls ?? [],
      inputMapping,
      plain,
      true,
      videoCreator.organization?.branding?.speakerFormat,
    );

    setMetadata(prev => ({
      ...prev,
      [compositionId]: {
        ...prev?.[compositionId],
        speakerLabel: plain.speakerLabel,
      },
    }));
  };

  async function downloadMedia(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    formatOverride?: MediaFormat,
  ) {
    await downloadMediaFromSource(e, {
      imageSource: rendererRef.current?.getSource(),
      itemId: data?.id,
      storyTitle: videoCreator.story?.title,
      quoteText: data?.quote,
      formatOverride,
      onDownloadStart: () => {
        setIsDownloading(true);
        setIsDownloadDropdownOpen(false);
      },
      onDownloadEnd: () => setIsDownloading(false),
      onDownloadSuccess: outputFormat => {
        analyticsStore.trackEvent({
          logMethod: 'info',
          eventName: 'shareable_image_download',
          label: 'Shareable Image download',
          transactionId: transactionId.current,
          extraProperties: {
            name,
            shareable: data,
            downloadFormat: outputFormat,
          },
        });
      },
    });
  }

  const dimContainerRatio = getOffsetsAndDimContainerRatio(currentAspectRatio);

  return (
    <Modal
      isOpen
      customWidth="calc(100% - 300px)"
      customHeight="calc(100% - 150px)"
      customMinHeight="400px"
      customMinWidth="600px"
    >
      <Main data-testid="quote-edit-modal">
        <Header>
          {editorStoreRef.current && (
            <UndoRedoButtonsContainer>
              <UndoRedoButtons
                canUndo={canUndo}
                canRedo={canRedo}
                getUndoLabel={() => '(Ctrl+Z)'}
                getRedoLabel={() => '(Ctrl+Shift+Z or Ctrl+Y)'}
                onUndo={() => editorStoreRef.current?.editor?.undo()}
                onRedo={() => editorStoreRef.current?.editor?.redo()}
                setHotkeys={true}
              />
            </UndoRedoButtonsContainer>
          )}
          <PreviewButton onClick={() => preview()}>
            <PlayIconPure />
            Preview
          </PreviewButton>
          <Dimensions>
            <DimensionsSelector
              dimensions={[
                ...Object.values(AspectRatio),
                ...Object.values(AdditionalAspectRatio),
              ]
                .filter(ar =>
                  contentStudioQuotesHide169And916
                    ? ar !== AspectRatio.AR_16_9 && ar !== AspectRatio.AR_9_16
                    : true,
                )
                .map(aspectRatio => ({
                  aspectRatio,
                  isPublished: true,
                }))}
              selectedDimension={currentAspectRatio}
              onSelectDimension={(dimension: string) =>
                updateAspectRatio(dimension as ExtendedAspectRatio)
              }
              theme="yellow"
            />
          </Dimensions>
          <ActionButtonsContainer>
            <SaveStatusGroup>
              <AutosaveStatusIcon
                status={status}
                lastSavedAt={lastSavedAt}
                // Portaled to `document.body`: without a modal-stack z it
                // opens behind this modal (PLAT-11654).
                popoverZIndex={CLIP_PREVIEW_Z.modalPopover}
              />
              <AutosaveStatusIndicator
                status={status}
                lastSavedAt={lastSavedAt}
              />
            </SaveStatusGroup>
            <ContentButton
              variant="primary"
              size="xs"
              icon="download"
              onClick={() => setIsDownloadDropdownOpen(true)}
              disabled={isDownloading}
              buttonLabel={isDownloading ? 'Downloading' : 'Download'}
            />
            <ClipDownloadDropdown
              onClick={(
                e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                format: MediaFormat,
              ) => {
                downloadMedia(e, format);
              }}
              isOpen={isDownloadDropdownOpen}
              setIsOpen={setIsDownloadDropdownOpen}
            />
          </ActionButtonsContainer>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </Header>
        <Content>
          <LeftPane>
            <MainRendererContainer data-testid="quote-canvas-area">
              <PlayerOverlay
                aspectRatio={currentAspectRatio as AspectRatio}
                dynamicScaleFactor={false}
                rendererElement={rendererRef.current?.element}
                dimContainerAspectRatio={dimContainerRatio}
              />
              <RendererContainer ref={footprintRef}>
                <RendererScaler
                  ref={rendererContainerRef}
                  style={{
                    width,
                    height,
                    transform: `scale(${scale})`,
                  }}
                />
              </RendererContainer>
            </MainRendererContainer>
          </LeftPane>

          <RightPane ref={sidebarRef}>
            <QuoteCardTabs activeTab={activeTab} setActiveTab={setActiveTab}>
              {activeTab => (
                <>
                  {activeTab === 'settings' && (
                    <QuoteCardSettingsPanel
                      compositionTemplate={activeCompositionTemplate}
                      activeElement={activeElement}
                      dimension={currentAspectRatio}
                      editorStore={editorStoreRef.current}
                      videoCreator={videoCreator}
                      rendererRef={rendererRef}
                      metadata={metadata || {}}
                      handleSpeakerControlChange={handleSpeakerControlChange}
                      onAfterCreate={handleTemplateSaved}
                      activeComposition={activeComposition}
                      editRevision={editRevision}
                    />
                  )}
                  {activeTab === 'transcript' && (
                    <QuoteCardTranscriptPanel quote={data.quote} />
                  )}
                  {activeTab === 'templates' && (
                    <QuoteCardTemplatesPanel
                      onAddComposition={handleAddComposition}
                      activeTemplateId={activeCompositionTemplate.id}
                    />
                  )}
                  {/* {activeTab === 'social-copy' && (
                    <TemplatesPlaceholder>
                      Social Copy content coming soon...
                    </TemplatesPlaceholder>
                  )} */}
                </>
              )}
            </QuoteCardTabs>
          </RightPane>
        </Content>
      </Main>
    </Modal>
  );
};

const Main = styled.div`
  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  text-align: initial;

  h2 {
    color: var(--primary-hover);
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    text-transform: uppercase;
  }
  height: 100%;
  width: 100%;
`;

const Dimensions = styled.div`
  margin-left: 192px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  padding: 12px 20px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  position: relative;
`;

const SaveStatusGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const MainRendererContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  flex: 1;
`;

const PreviewButton = styled.button`
  display: flex;
  height: 28px;
  padding: 0 8px;
  gap: 4px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 1px solid color-mix(in srgb, var(--text-muted) 50%, transparent);
  background: transparent;
  color: var(--text-muted);
  font-family: Inter;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;

  transition: all 0.2s ease;

  svg {
    width: 12px;
    height: 12px;
    path {
      transition: stroke 0.2s ease;
      stroke: var(--text-muted);
    }
  }

  &:hover {
    color: var(--accent);
    border-color: var(--accent);

    svg {
      path {
        stroke: var(--accent);
      }
    }
  }
`;

const Content = styled.div`
  display: flex;
  gap: 24px;
  height: calc(100% - 60px);
  width: 100%;
  padding: 0 20px 12px 20px;
`;

const LeftPane = styled.div`
  position: relative;
  flex: 1;
  height: 100%;
  min-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UndoRedoButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 96px;
`;

const RendererContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  aspect-ratio: 1 / 1;
`;

// Sized/scaled inline via usePreviewSupersample so the interactive iframe
// rasterizes at a higher resolution than the display box, then CSS-downscales
// to fit. Scaling maps pointer events through the transform, so drag/resize
// still land correctly.
const RendererScaler = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  overflow: hidden;
`;

const RightPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  height: 100%;
  width: 400px;
  min-width: 350px;
  flex-shrink: 0;
  padding-right: 20px;
  padding-left: 2px;
  padding-top: 48px;
`;

const CloseButton = styled.button`
  background-color: transparent;
  border: 0;
  outline: 0;
  cursor: pointer;
  margin: 4px;
  color: var(--text-muted);
  padding: var(--spacing-sm);
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

export default EditSharableImageModal;
